import {
  Button,
  type ExportScope,
  FullScreenLoading,
  Icon,
  ServerTable,
  SvgIcon,
  Text,
} from '@civilgrid/ui/components';
import { buildSearchPayload, isEmptyFilterGroup, useTableStoreState } from '@civilgrid/ui/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { debounce } from 'lodash';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
  type BulkConstructionDatesUpdates,
  bulkUpdateConstructionDates,
  type SummaryExportColumnDef,
} from '~/api/intelligenceSummariesV3';
import { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { getProjectRowLabel } from '~/common/projectUtils';
import type { OrganizationDetail, PlanStatus, TLoadedState } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { setConflictsSummaryViewPref } from '~/hooks/useConflictsSummaryPath';
import { useNewConflictSummaryFlag } from '~/hooks/useNewConflictSummaryEnabled';
import { useOrgFeatureFlag } from '~/hooks/useOrgFeatureFlag';
import store from '~/store';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';
import { buildViewedProperties } from './analytics';
import type { SummaryColumn } from './columns';
import { EditConstructionDatesDialog } from './EditConstructionDatesDialog';
import { EditPlanStatusDialog } from './EditPlanStatusDialog';
import { type SummaryRow, withFeatureSubRows } from './featureRows';
import {
  prefetchSummariesQuery,
  useExportSummariesTable,
  useGetIntelligenceSummaries,
  useGetIntelligenceSummaryMeta,
  useInvalidateSummaries,
  useSummaryPolling,
  visiblePollIds,
} from './queries';
import { useBulkEditConstructionDates } from './useBulkEditConstructionDates';
import { useBulkEditPlanStatus } from './useBulkEditPlanStatus';
import { useSummaryColumns } from './useSummaryColumns';
import {
  applyMyProjectsFilter,
  createMyProjectsFilter,
  getActiveYearFilter,
  getYearFilterValue,
  isMyProjectsFilterActive,
  removeMyProjectsFilter,
  setYearFilter,
} from './utils';
import { YearsFilterDropdown } from './YearsFilterDropdown';

const TABLE_ID = 'conflict-summary-table';

const getSummaryRowLabel = (row: SummaryRow) => getProjectRowLabel(row.project);

/** Recursively map table columns to export column defs, preserving grouped headers. */
function toExportColumnDefs(cols: SummaryColumn[]): SummaryExportColumnDef[] {
  return cols.map((col) => {
    const meta = col.meta as { exportLabel?: string } | undefined;
    const legacyExportLabel = (col as { exportLabel?: string }).exportLabel;
    const header = typeof col.header === 'string' ? col.header : undefined;
    const def: SummaryExportColumnDef = {
      id: col.id,
      header: meta?.exportLabel ?? legacyExportLabel ?? header ?? col.id,
    };
    const children = (col as { columns?: SummaryColumn[] }).columns;
    if (children?.length) def.columns = toExportColumnDefs(children);
    return def;
  });
}

/**
 * New Conflict Summary page: same view as /conflictsSummary but the table is
 * the shared ServerTable with all state server-side (v3 search / meta /
 * export endpoints). Gated by the ConflictsSummary org flag and the
 * new-conflict-summary-enabled PostHog flag.
 */
export default function ConflictsSummaryV2() {
  useOrgFeatureFlag({ flag: OrgFeatureFlag.ConflictsSummary, redirectTo: '/home' });
  const { resolved, enabled } = useNewConflictSummaryFlag();
  const navigate = useNavigate();

  // Redirect flag-OFF users to the classic page after the flag resolves.
  useEffect(() => {
    if (resolved && !enabled) {
      navigate({ to: '/conflictsSummary', replace: true });
    }
  }, [resolved, enabled, navigate]);

  if (!resolved || !enabled) {
    return <FullScreenLoading />;
  }

  return <ConflictsSummaryV2Page />;
}

function ConflictsSummaryV2Page() {
  const headingId = useId();
  const { currentOrganizationId, currentOrganizationDetail, me } = useSnapshot(store) as typeof store;

  const orgDetail = currentOrganizationDetail as TLoadedState & OrganizationDetail;
  const conflictsSummaryConfig = orgDetail.conflictsSummaryConfig;

  const stateControl = useTableStoreState({
    initialSorting: [{ id: 'percentConflict', desc: true }],
    storageKey: TABLE_ID,
  });
  const { pagination, sorting, filters, globalFilter, setFilters, setGlobalFilter, resetFilters } = stateControl;

  const { data, isLoading, isFetching, isPlaceholderData, error } = useGetIntelligenceSummaries({
    organizationId: currentOrganizationId,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    sorting,
    filters,
    globalFilter,
    enabled: !!currentOrganizationId,
  });

  const summaryMeta = useGetIntelligenceSummaryMeta(currentOrganizationId);

  const invalidate = useInvalidateSummaries();
  const { addToPoll } = useSummaryPolling({
    organizationId: currentOrganizationId,
    summaries: data?.summaries,
  });
  // Inline date-cell edits reuse the bulk endpoint with a single-summary selection.
  const updateDates = useCallback(
    async (summaryId: string, updates: BulkConstructionDatesUpdates) => {
      await bulkUpdateConstructionDates(currentOrganizationId, {
        selection: { mode: 'partial', summaryIds: [summaryId] },
        updates,
      });
    },
    [currentOrganizationId],
  );

  const callbacks = useMemo(
    () => ({ onEdited: invalidate, addToPoll, updateDates }),
    [invalidate, addToPoll, updateDates],
  );

  const [selectionResetKey, setSelectionResetKey] = useState(0);

  const getSummaryById = useCallback((id: string) => data?.summaries.find((s) => s.id === id), [data]);

  const buildSearchForBulk = useCallback(
    () => buildSearchPayload({ pageIndex: 0, pageSize: pagination.pageSize, sorting, filters, globalFilter }),
    [pagination.pageSize, sorting, filters, globalFilter],
  );

  const bulkEditDates = useBulkEditConstructionDates({
    organizationId: currentOrganizationId,
    getSummaryById,
    buildSearchForBulk,
    onApplied: (result) => {
      // Only current-page rows go into the poll; see visiblePollIds.
      for (const summaryId of visiblePollIds(result.summaryIds, data?.summaries)) addToPoll(summaryId);
      invalidate();
      setSelectionResetKey((key) => key + 1);
    },
  });

  const { columns, statusLabels } = useSummaryColumns({
    conflictsSummaryConfig,
    callbacks,
  });

  const statusOptions = useMemo(
    () => Object.entries(statusLabels).map(([value, label]) => ({ value: value as PlanStatus, label })),
    [statusLabels],
  );

  const bulkEditPlanStatus = useBulkEditPlanStatus({
    organizationId: currentOrganizationId,
    getSummaryById,
    buildSearchForBulk,
    // Plan status is not a conflict-trigger field, so no rows are polled.
    onApplied: () => {
      invalidate();
      setSelectionResetKey((key) => key + 1);
    },
  });

  // The construction-dates bulk flow (hook + dialog) stays wired but unlisted here.
  const bulkActions = useMemo(
    () => [
      {
        label: 'Edit Plan Status',
        icon: 'edit' as const,
        onClick: bulkEditPlanStatus.open,
        testId: 'bulk-edit-plan-status',
      },
    ],
    [bulkEditPlanStatus.open],
  );

  const exportMutation = useExportSummariesTable(currentOrganizationId, orgDetail?.name);

  const isFirstSortRender = useRef(true);
  useEffect(() => {
    if (isFirstSortRender.current) {
      isFirstSortRender.current = false;
      return;
    }
    if (sorting[0]) {
      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummarySortChanged, {
        column_id: sorting[0].id,
        direction: sorting[0].desc ? 'desc' : 'asc',
      });
    }
  }, [sorting]);

  const trackSearch = useMemo(
    () =>
      debounce((value: string) => {
        capturePosthogEvent(POSTHOG_EVENTS.conflictsSummarySearchChanged, {
          has_query: value.length > 0,
          query_length: value.length,
        });
      }, 500),
    [],
  );
  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    trackSearch(globalFilter);
  }, [globalFilter, trackSearch]);
  useEffect(() => () => trackSearch.cancel(), [trackSearch]);

  const activeYear = useMemo(() => getActiveYearFilter(filters), [filters]);

  const yearOptions = summaryMeta.data?.constructionYears ?? [];
  const selectedYear = useMemo(() => getYearFilterValue(filters), [filters]);
  const onYearFilterChange = useCallback(
    (year: string | null) => {
      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryYearFilterChanged, {
        year: year ? Number(year) : null,
        previous_year: selectedYear ? Number(selectedYear) : null,
      });
      setFilters(setYearFilter(filters, year));
    },
    [filters, selectedYear, setFilters],
  );

  const queryClient = useQueryClient();
  const currentUserId = me?.id ?? null;
  const myProjectsFilter = useMemo(
    () => (currentUserId ? createMyProjectsFilter(currentUserId, me?.name || me?.email || 'Me') : null),
    [currentUserId, me?.name, me?.email],
  );
  const isMyProjectsActive = useMemo(() => isMyProjectsFilterActive(filters, currentUserId), [filters, currentUserId]);
  const onMyProjects = useCallback(() => {
    if (!myProjectsFilter) return;
    capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryMyProjectsToggled, {
      organization_id: currentOrganizationId,
      active: !isMyProjectsActive,
    });
    setFilters(isMyProjectsActive ? removeMyProjectsFilter(filters) : applyMyProjectsFilter(filters, myProjectsFilter));
  }, [myProjectsFilter, isMyProjectsActive, filters, setFilters, currentOrganizationId]);
  const onMyProjectsMouseEnter = useCallback(() => {
    if (!myProjectsFilter || !currentOrganizationId) return;
    prefetchSummariesQuery(queryClient, {
      organizationId: currentOrganizationId,
      pageIndex: 0,
      pageSize: pagination.pageSize,
      sorting,
      filters: applyMyProjectsFilter(filters, myProjectsFilter),
    });
  }, [queryClient, myProjectsFilter, filters, currentOrganizationId, pagination.pageSize, sorting]);

  // Pageview fires once, when the first summaries response (or its error)
  // lands, so it carries table context instead of empty properties.
  const hasCapturedView = useRef(false);
  useEffect(() => {
    if (hasCapturedView.current || (!data && !error)) return;
    hasCapturedView.current = true;
    capturePosthogEvent(
      POSTHOG_EVENTS.conflictsSummaryV2Viewed,
      buildViewedProperties({
        organizationId: currentOrganizationId,
        totalProjects: summaryMeta.data?.total ?? null,
        summaries: data?.summaries ?? [],
        yearFilter: selectedYear,
        myProjectsActive: isMyProjectsActive,
        searchLength: globalFilter.length,
      }),
    );
  }, [data, error, currentOrganizationId, summaryMeta.data, selectedYear, isMyProjectsActive, globalFilter]);

  // Scope of the last export click, so only that export button shows the pending spinner.
  const [exportScope, setExportScope] = useState<ExportScope>('all');
  const onExport = ({ visible }: { visible: boolean }) => {
    setExportScope(visible ? 'visible' : 'all');
    const startedAt = Date.now();
    const rowCount = visible ? (data?.count ?? 0) : (summaryMeta.data?.total ?? 0);
    capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryExportClicked, {
      row_count: rowCount,
      year_filter: activeYear ?? null,
    });
    const search = visible
      ? buildSearchPayload({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          sorting,
          filters,
          globalFilter,
        })
      : buildSearchPayload({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          sorting,
        });
    exportMutation.mutate(
      { columnDefs: toExportColumnDefs(columns), search },
      {
        onSuccess: () => {
          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryExportSucceeded, {
            row_count: rowCount,
            duration_ms: Date.now() - startedAt,
          });
        },
        onError: (err) => {
          Notify.error({
            message: 'An error occurred while exporting the conflicts summary. Please contact support.',
          });
          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryExportFailed, {
            row_count: rowCount,
            error_message: err instanceof Error ? err.message : 'unknown',
          });
        },
      },
    );
  };

  // Skeletons only on view transitions (new query key); same-key background refetches keep the rows rendered.
  const isViewTransition = isFetching && isPlaceholderData;

  const tableData = useMemo(
    () => ({
      data: withFeatureSubRows(data?.summaries ?? []),
      rowCount: data?.count ?? 0,
      isLoading,
      isFetching: isViewTransition,
      error: error ?? null,
    }),
    [data, isLoading, isViewTransition, error],
  );

  const handleClearAllFilters = useCallback(() => {
    resetFilters();
    setGlobalFilter('');
  }, [resetFilters, setGlobalFilter]);

  // Year and My Projects shortcuts live inside `filters`, so the group check covers them.
  const hasActiveFilters = useMemo(
    () => !isEmptyFilterGroup(filters) || globalFilter.trim().length > 0,
    [filters, globalFilter],
  );

  const isMyProjectsOnlyFilter = useMemo(
    () => isMyProjectsActive && isEmptyFilterGroup(removeMyProjectsFilter(filters)) && !globalFilter,
    [isMyProjectsActive, filters, globalFilter],
  );

  const emptyState = useMemo(() => {
    if (tableData.rowCount > 0 || tableData.isLoading || tableData.isFetching) return null;
    if (isMyProjectsOnlyFilter) {
      return (
        <div className="flex flex-col items-center gap-4 text-center">
          <Text className="text-grey-80">Nothing has been assigned to you yet.</Text>
        </div>
      );
    }
    if (hasActiveFilters) {
      return (
        <div className="flex flex-col items-center gap-4 text-center">
          <Text className="text-grey-80">No records to show based on current filters.</Text>
          <Button
            variant="secondary"
            size="small"
            onClick={handleClearAllFilters}
            data-testid="conflicts-summary-clear-all-filters"
          >
            <Icon icon="crossCircled" size={14} /> Clear all filters
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <Text className="text-grey-80">
          No conflict summaries yet. They appear here once your organization's projects have been analyzed.
        </Text>
      </div>
    );
  }, [tableData, isMyProjectsOnlyFilter, hasActiveFilters, handleClearAllFilters]);

  return (
    <ProtectedRouteLayout includeOrgProjectSwitcher={true} includeSearch={false} fluidHeight>
      <div className="flex h-full min-h-0 flex-col space-y-3 p-4" data-testid="conflicts-summary-v2-page">
        <div className="flex items-center justify-between" data-testid="conflicts-summary-v2-header">
          <h1 id={headingId} className="text-xl font-semibold text-blue-80">
            Conflicts Summary
          </h1>
          <div className="flex items-center gap-2">
            <Button asChild variant="secondary" data-testid="conflicts-summary-v2-back-to-classic">
              <Link
                to="/conflictsSummary"
                onClick={() => {
                  setConflictsSummaryViewPref('classic');
                  capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryV2BackToClassicClicked, {});
                }}
              >
                <Icon icon="update" size={14} /> Classic View
              </Link>
            </Button>
            <Button asChild variant="secondary" data-testid="conflicts-summary-data-refresh-status-button">
              <Link
                to="/conflictsSummary/dataRefreshStatus"
                onClick={() =>
                  capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryDataRefreshStatusClicked, {
                    organization_id: currentOrganizationId,
                  })
                }
              >
                <SvgIcon icon="database" size={14} alt="" /> Data Refresh Status
              </Link>
            </Button>
          </div>
        </div>

        <div className="relative flex h-full min-h-0 grow flex-col" data-testid="conflicts-summary-v2-table">
          <ServerTable
            id={TABLE_ID}
            aria-labelledby={headingId}
            columns={columns}
            data={tableData}
            stateControl={stateControl}
            getRowId={(row: SummaryRow) => row.id}
            getRowLabel={getSummaryRowLabel}
            enableRowSelection={true}
            // Accordion feature child rows carry no checkbox; selection is parent rows only.
            enableSubRowSelection={false}
            selectionResetKey={selectionResetKey}
            bulkActions={bulkActions}
            enableExpanding
            getSubRows={(row: SummaryRow) => row.subRows}
            className="h-full"
            isLoading={tableData.isLoading}
            isFetching={tableData.isFetching}
            emptyState={emptyState}
            onExportAll={() => onExport({ visible: false })}
            onExportVisible={() => onExport({ visible: true })}
            exporting={exportMutation.isPending ? exportScope : undefined}
            onMyFilters={onMyProjects}
            onMyFiltersMouseEnter={onMyProjectsMouseEnter}
            myFiltersLabel={
              <>
                <Icon icon="avatar" size={14} /> <Text className="text-xs">My Projects</Text>
              </>
            }
            isMyFiltersActive={isMyProjectsActive}
            toolbarContent={
              <YearsFilterDropdown years={yearOptions} value={selectedYear} onChange={onYearFilterChange} />
            }
          />
          <EditConstructionDatesDialog
            open={bulkEditDates.isOpen}
            onClose={bulkEditDates.close}
            constructionYear={bulkEditDates.constructionYear}
            onConstructionYearChange={bulkEditDates.setConstructionYear}
            constructionStartDate={bulkEditDates.constructionStartDate}
            onConstructionStartDateChange={bulkEditDates.setConstructionStartDate}
            constructionEndDate={bulkEditDates.constructionEndDate}
            onConstructionEndDateChange={bulkEditDates.setConstructionEndDate}
            canSubmit={bulkEditDates.canSubmit}
            onSubmit={bulkEditDates.onSubmit}
            isPending={bulkEditDates.isPending}
          />
          <EditPlanStatusDialog
            open={bulkEditPlanStatus.isOpen}
            onClose={bulkEditPlanStatus.close}
            planStatus={bulkEditPlanStatus.planStatus}
            onPlanStatusChange={bulkEditPlanStatus.setPlanStatus}
            statusOptions={statusOptions}
            canSubmit={bulkEditPlanStatus.canSubmit}
            onSubmit={bulkEditPlanStatus.onSubmit}
            isPending={bulkEditPlanStatus.isPending}
          />
        </div>
      </div>
    </ProtectedRouteLayout>
  );
}
